import * as React from 'react';

import { useDeviceType } from '@cian/ui-kit/responsive';

import { MainScreenMobile, MainScreenDesktop } from '../../components/MainScreen';
import { ProgressBarContainer } from '../ProgressBarContainer';
import { Provider, useFormController, FormContainer } from '../FormContainer';
import { BackToPageContainer } from '../BackToPageContainer';
import { TFormStoreData } from '../../types/form';
import { prevQuestionTexts } from '../../constants/prevQuestionTexts';
import { submitForm } from '../../actions/submitForm';
import { useOnRenderedInBrowser } from './utils/useOnRenderedInBrowser';
import { PageBaseLayout } from '../../components/PageBaseLayout';
import { showGoBackBtn } from './utils/showGoBackBtn';
import { Intro } from '../../components/Intro';

interface IPageContainerWithDataProps {
  formData: TFormStoreData;
  withIntroScreen: boolean;
}

export const PageContainerWithData: React.FC<IPageContainerWithDataProps> = ({ formData, withIntroScreen }) => {
  const formContext = useFormController(formData, submitForm);
  const { setPrevStep, quizStarted, quizCompleted, stepsHistory } = formContext;
  const deviceType = useDeviceType();
  const MainScreen = deviceType === 'desktop' ? MainScreenDesktop : MainScreenMobile;

  useOnRenderedInBrowser(formData, formContext);

  const main = React.useMemo(() => {
    if (quizCompleted) {
      return <FormContainer />;
    }

    return (
      <MainScreen
        main={withIntroScreen && !quizStarted ? <Intro /> : <FormContainer />}
        backToPage={<BackToPageContainer />}
        progressBar={quizStarted || deviceType === 'desktop' ? <ProgressBarContainer /> : null}
        showGoBackBtn={showGoBackBtn(stepsHistory)}
        handleGoBack={setPrevStep}
        goBackText={prevQuestionTexts[deviceType]}
      />
    );
  }, [stepsHistory, deviceType, quizStarted, withIntroScreen, quizCompleted, setPrevStep]);

  return (
    <Provider value={formContext}>
      <PageBaseLayout quizCompleted={formContext.quizCompleted}>{main}</PageBaseLayout>
    </Provider>
  );
};

PageContainerWithData.displayName = 'PageContainerWithData';
